// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import type { FormEvent } from 'react';
import { useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import type { User } from '../types/auth';

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

export function RegisterForm({ onSuccess, onSwitchToLogin }: RegisterFormProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setAuth } = useAuthStore();

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain an uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain a lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain a number';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Validate invite code (XXXX-XXXX format = 9 chars)
    const codePattern = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/;
    if (!inviteCode || !codePattern.test(inviteCode)) {
      setError('Please enter a valid invite code (XXXX-XXXX format)');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.register({
        username,
        password,
        invite_code: inviteCode,
      });

      const user: User = {
        user_id: response.user_id,
        username: response.username,
        permissions: ['user'], // New users get default permission
        theme_preference: response.theme_preference,
        theme_primary_color: response.theme_primary_color,
        profile_emoji: response.profile_emoji,
        namespace: response.namespace,
      };

      setAuth(user, response.access_token, response.refresh_token);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
      <Input
        label="Username"
        type="text"
        value={username}
        onChange={e => setUsername(e.target.value)}
        placeholder="Choose a username"
        required
        minLength={3}
        maxLength={50}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        data-testid="register-username"
      />
      <Input
        label="Password"
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder="Create a password"
        required
        minLength={8}
        autoComplete="new-password"
        data-testid="register-password"
      />
      <Input
        label="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        placeholder="Confirm your password"
        required
        autoComplete="new-password"
        data-testid="register-confirm-password"
      />
      <div className="text-xs font-bold" style={{ color: '#666666' }}>
        Password must be at least 8 characters with uppercase, lowercase, and number.
      </div>
      <Input
        label="Invite Code"
        type="text"
        value={inviteCode}
        onChange={e => setInviteCode(e.target.value.toUpperCase())}
        placeholder="XXXX-XXXX"
        required
        minLength={9}
        maxLength={9}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        data-testid="register-invite-code"
      />
      {error && (
        <p className="text-sm font-bold" style={{ color: '#EF4444' }}>
          {error}
        </p>
      )}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full font-bold"
        data-testid="register-submit"
      >
        {isSubmitting ? 'Creating account...' : 'Create Account'}
      </Button>

      {onSwitchToLogin && (
        <p className="text-center text-sm mt-4 font-bold" style={{ color: '#000000' }}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="underline hover:no-underline font-bold"
            style={{ color: '#FF6EC7' }}
          >
            Sign in
          </button>
        </p>
      )}
    </form>
  );
}
